// Client-facing AI Credit Profile Analyzer (Sprint 2 / 7B).
// Upload PDF / image / CSV credit report → Claude analyzes → summary card +
// dispute candidates surfaced for admin follow-up.
import { useCallback, useEffect, useRef, useState } from "react";
import BrandShell from "../components/BrandShell";
import { useAuth, apiCall } from "../auth/AuthContext";
import { CheckCircle2, FileText, Loader2, Sparkles, Upload, AlertCircle, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ClientCreditAnalyzer() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    const [p, h] = await Promise.all([
      apiCall("/api/credit/profile").catch(() => ({ profile: null })),
      apiCall("/api/credit/history").catch(() => ({ records: [] })),
    ]);
    return { profile: p.profile || null, history: h.records || [] };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { profile: p, history: h } = await refresh();
      if (!cancelled) { setProfile(p); setHistory(h); }
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, 5);
    setFiles(picked);
    setError("");
  };

  const submit = async () => {
    if (!files.length) { setError("Pick at least one credit report file."); return; }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const token = localStorage.getItem("jc3_token");
      const res = await fetch(`${API}/api/credit/upload`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Upload failed (${res.status})`);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      const { profile: p, history: h } = await refresh();
      setProfile(p); setHistory(h);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setUploading(false);
    }
  };

  const s = profile?.summary || {};
  const candidates = profile?.profile?.dispute_candidates || [];

  return (
    <BrandShell dashboardChrome>
      <div className="bg-jc-black min-h-[calc(100vh-5rem)]">
        <div className="px-6 md:px-12 py-12 max-w-6xl mx-auto" data-testid="client-credit-analyzer">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold mb-2">◆ AI Credit Insights</p>
        <h1 className="font-display text-jc-white" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", lineHeight: 0.95 }}>
          Your credit profile, decoded in seconds.
        </h1>
        <p className="mt-4 text-jc-muted max-w-2xl">
          Upload your most recent credit report from any bureau or service. Our AI gives you an educational read of what's shaping your profile — items to review for accuracy and recommended next steps. You stay in control of every decision.
        </p>

        {/* Upload card */}
        <section className="mt-10 border border-jc-gold/25 bg-jc-navy/50 p-8 rounded-xl" data-testid="credit-upload-card">
          <div className="flex items-center gap-3 mb-4">
            <Upload size={18} className="text-jc-gold" />
            <h2 className="font-display text-2xl text-jc-white">Upload credit report</h2>
          </div>
          <p className="text-sm text-jc-muted mb-5">
            PDF, image scan (PNG/JPG), or CSV. Up to 5 files, 5 MB each. Bureau reports work best (Experian, Equifax, TransUnion, MyFICO, IdentityIQ).
          </p>
          <input
            ref={fileInputRef}
            type="file"
            data-testid="credit-file-input"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,application/pdf,image/*,text/csv"
            onChange={onPick}
            className="block w-full text-sm text-jc-white file:mr-4 file:py-2.5 file:px-5 file:rounded-none file:border-0 file:bg-jc-gold file:text-jc-black file:font-mono file:text-xs file:uppercase file:tracking-[0.18em] file:cursor-pointer"
          />
          {files.length > 0 && (
            <ul className="mt-4 space-y-1 text-xs font-mono text-jc-muted" data-testid="credit-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${f.size}-${i}`}>◆ {f.name} <span className="text-jc-gold">{(f.size / 1024).toFixed(0)} KB</span></li>
              ))}
            </ul>
          )}
          {error && (
            <div className="mt-4 flex items-start gap-2 text-sm text-red-300" data-testid="credit-upload-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
          <button
            data-testid="credit-upload-submit"
            disabled={uploading || files.length === 0}
            onClick={submit}
            className="mt-6 jc-btn jc-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? <><Loader2 size={14} className="animate-spin" /> Analyzing — this can take 30–60s…</> : <><Sparkles size={14} /> Analyze with AI</>}
          </button>
          <p className="mt-3 text-[11px] text-jc-muted/80 font-mono uppercase tracking-[0.18em]">
            ◆ Encrypted in transit and at rest. Never shared.
          </p>
        </section>

        {/* Summary card */}
        {profile && (
          <section className="mt-10" data-testid="credit-summary-card">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp size={18} className="text-jc-gold" />
              <h2 className="font-display text-2xl text-jc-white">Your latest analysis</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="FICO Range" value={s.fico_range || "—"} accent />
              <Stat label="Headline Score" value={s.headline_score ?? "—"} sub={s.bureau_with_score} />
              <Stat label="Tradelines" value={s.tradeline_count ?? "—"} />
              <Stat label="Items to Review" value={s.negative_items_count ?? 0} />
              <Stat label="Collections" value={s.collections_count ?? 0} />
              <Stat label="Public Records" value={s.public_records_count ?? 0} />
              <Stat label="Hard Inquiries (24mo)" value={s.inquiries_count_24mo ?? 0} />
              <Stat label="Accuracy Review Items" value={candidates.length} accent />
            </div>

            {(s.top_recommendations || []).length > 0 && (
              <div className="mt-6 border border-jc-gold/20 bg-jc-black/40 p-6 rounded-xl">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold mb-3">◆ Top recommendations</p>
                <ul className="space-y-2 text-sm text-jc-white">
                  {(s.top_recommendations || []).map((r, i) => (
                    <li key={`${i}-${String(r).slice(0, 40)}`} className="flex gap-3"><CheckCircle2 size={16} className="text-jc-gold shrink-0 mt-0.5" /><span>{r}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {candidates.length > 0 && (
              <div className="mt-6 border border-jc-gold/20 bg-jc-black/40 p-6 rounded-xl" data-testid="credit-dispute-candidates">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold mb-3">◆ Items flagged for accuracy review · {candidates.length}</p>
                <p className="text-xs text-jc-muted mb-4">These are items our AI suggests you review for accuracy. CEO Jace will walk you through what they mean and your options — you decide how to address each one.</p>
                <div className="space-y-3">
                  {candidates.slice(0, 10).map((c, i) => (
                    <div key={`${i}-${c.creditor || c.account || c.severity || "cand"}`} className="border-l-2 border-jc-gold/60 pl-4 py-1">
                      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-jc-muted">
                        <span className={`px-2 py-0.5 rounded ${c.severity === "high" ? "bg-red-500/20 text-red-200" : c.severity === "medium" ? "bg-amber-500/20 text-amber-200" : "bg-jc-muted/20 text-jc-muted"}`}>{c.severity || "n/a"}</span>
                        <span>{c.item_type}</span>
                        <span>·</span>
                        <span className="text-jc-gold">{c.creditor_or_furnisher}</span>
                        {c.account_number_masked && <span>· {c.account_number_masked}</span>}
                      </div>
                      <p className="mt-1.5 text-sm text-jc-white">{c.fcra_violation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* History */}
        {history.length > 1 && (
          <section className="mt-10" data-testid="credit-history">
            <h2 className="font-display text-2xl text-jc-white mb-4">Analysis history</h2>
            <ul className="space-y-2 text-sm">
              {history.map((h) => (
                <li key={h.id} className="flex items-center justify-between border border-jc-gold/15 px-4 py-3">
                  <span className="text-jc-white">{new Date(h.created_at).toLocaleString()}</span>
                  <span className={`font-mono text-[11px] uppercase tracking-[0.18em] ${h.status === "analyzed" ? "text-emerald-300" : h.status === "error" ? "text-red-300" : "text-jc-muted"}`}>{h.status}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!profile && (
          <section className="mt-10 text-center py-12 border border-dashed border-jc-gold/25 rounded-xl" data-testid="credit-empty">
            <FileText size={42} className="mx-auto text-jc-gold/50 mb-4" />
            <p className="text-jc-muted">No analysis yet. Upload your first credit report above to get started.</p>
          </section>
        )}
        </div>
      </div>
    </BrandShell>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-jc-gold/40 bg-jc-gold/5" : "border-jc-gold/15 bg-jc-black/30"} rounded-xl`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-jc-muted">{label}</p>
      <p className={`mt-1 font-display ${accent ? "text-jc-gold" : "text-jc-white"} text-2xl`}>{value}</p>
      {sub && <p className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.18em] text-jc-muted">{sub}</p>}
    </div>
  );
}
